<template>
  <div v-if="chartData.data.length">
    <ChartTitle>
      {{ title }}<ChartSubtitle v-if="this.subtitle">{{ this.subtitle }}</ChartSubtitle>
    </ChartTitle>
    <ChartContainer>
      <AreaChart
        v-bind="$props"
        :data="chartData.data"
        :filteredData="chartData.filteredData"
        :xProp="chartData.axis"
        :series="chartData.series"
        :isTotal="chartData.isTotal"
        :topAxisVisible="false"
        :rightAxisVisible="false"
        :strokeVisible="true"
        :fill="() => 'transparent'"
        :strokeWidth="2"
        :stroke-dashed-array="'2'"
        :axesWrapper="axesWrapper"
        :gridWrapper="gridWrapper"
        :yGridVisible="true"
        :paddingLeft="70"
        :stroke="getColor"
      />
    </ChartContainer>
  </div>
</template>

<script>
import { indexBy, prop, filter, pipe, gte } from 'ramda';
import { scaleLinear } from 'd3-scale';
import { interpolateHcl } from 'd3-interpolate';

import AreaChart from '../charts/areaChart/areaChart.component';
import { MOCKS_LIST } from '@/fixtures/chartData';
import { AxesWrapper, GridWrapper } from '../charts/axes/axes.styles';
import { YEAR_FIELD } from '../../constants';
import { ChartContainer, ChartTitle, ChartSubtitle } from './lineChart.styles';
import { mapGetters } from 'vuex';
import { PREFIX as REGIONS_STORE } from '../../../modules/regions';
import { GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC, GET_YEAR_FILTER } from '@/modules/regions/regions.getters';
import { getMultiSeriesData, getRegionField, getSummedData, getValueField } from '../../utils/helpers';

export default {
  props: {
    title: { type: String, default: 'A Look At Export Jobs (Direct)' },
    subtitle: { type: String, default: '' },
    data: { type: Array, default: () => [] },
    theme: { type: Object, default: () => {} },
  },
  components: { AreaChart, ChartContainer, ChartTitle, ChartSubtitle },
  data() {
    return {
      dataOptions: indexBy(prop('name'), MOCKS_LIST),
      axesWrapper: AxesWrapper,
      gridWrapper: GridWrapper,
    };
  },
  computed: {
    ...mapGetters(REGIONS_STORE, [GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC, GET_YEAR_FILTER]),
    chartData() {
      const valueField = getValueField(this[GET_SELECTED_SUBTOPIC]);
      const regionField = getRegionField(this[GET_SELECTED_SUBTOPIC]);
      const selectedFilters = this[GET_SELECTED_FILTERS];
      const isTotal = !selectedFilters.length;
      const filterData = filter(pipe(prop(YEAR_FIELD), year => parseInt(year, 10), gte(this[GET_YEAR_FILTER])));

      if (isTotal) {
        const summedData = getSummedData({ data: this.data, groupByField: YEAR_FIELD, valueField });
        const summedFilteredData = filterData(summedData);

        return {
          series: [valueField],
          axis: YEAR_FIELD,
          data: summedData.filter(item => item[YEAR_FIELD] !== 'null'),
          filteredData: summedFilteredData,
          isTotal,
        };
      }

      const multiData = getMultiSeriesData({
        data: this.data,
        groupByField: YEAR_FIELD,
        valueField,
        selectedFilters: this[GET_SELECTED_FILTERS],
        regionField,
      });

      return {
        series: selectedFilters,
        axis: YEAR_FIELD,
        data: multiData,
        filteredData: filterData(multiData),
        isTotal,
      };
    },
    colorScale() {
      const max = this.chartData.series.length;

      return scaleLinear()
        .domain([0, max])
        .range([this.theme.mainColor, this.theme.lightColor])
        .interpolate(interpolateHcl);
    },
  },
  methods: {
    getColor({ serieIndex }) {
      return this.colorScale(serieIndex);
    },
  },
};
</script>
